import React, { useState } from 'react';
import { forumHelpers, ForumReply } from '../lib/supabase';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { X, Save, Loader2 } from 'lucide-react';

interface ReplyEditorProps {
  reply: ForumReply | null;
  onClose: () => void;
  onReplyUpdated: (updatedReply: ForumReply) => void;
}

const ReplyEditor: React.FC<ReplyEditorProps> = ({ 
  reply, 
  onClose, 
  onReplyUpdated 
}) => {
  const [content, setContent] = useState(reply?.content || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!reply) {
      setError('Resposta não encontrada');
      return;
    }

    if (!content.trim()) {
      setError('Por favor, preencha o conteúdo da resposta');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error } = await forumHelpers.updateReply(reply.id, {
        content: content.trim()
      });

      if (error) {
        setError('Erro ao atualizar resposta');
        console.error('Erro ao atualizar resposta:', error);
      } else if (data) {
        onReplyUpdated(data);
        onClose();
      }
    } catch (err) {
      setError('Erro inesperado ao atualizar resposta');
      console.error('Erro inesperado:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-[#121316] rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[#1f2329]">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white">
              Editar Resposta
            </h2>
            <Button
              variant="ghost"
              onClick={onClose}
              className="text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-red-400">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="content" className="text-white">Conteúdo</Label>
              <Textarea
                id="content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Escreva o conteúdo da resposta..."
                rows={8}
                className="bg-[#0f1114] border-[#1f2329] text-white placeholder-gray-500"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center gap-2"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {loading ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
              <Button
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReplyEditor; 